import Stripe from 'stripe';
import { z } from 'zod';

export type SubscriptionStatus = Stripe.Subscription.Status
export const subscriptionStatuses = ['active', 'trialing', 'past_due', 'unpaid', 'incomplete', 'incomplete_expired', 'canceled', 'paused', 'incomplete_expired'] as const;

export const subscriptionPlanSchema = z.enum(['starter', 'essential', 'advanced', 'enterprise']);
export type SubscriptionPlan = z.infer<typeof subscriptionPlanSchema>

export const priceLookupKeySchema = z.enum(['starter_yearly', 'starter_monthly', 'essential_yearly', 'essential_monthly', 'advanced_monthly', 'advanced_yearly', 'enterprise_monthly', 'enterprise_yearly']);
export type PriceLookupKey = z.infer<typeof priceLookupKeySchema>

export const subscriptionIntervalSchema = z.enum(['year', 'month']);
export type SubscriptionInterval = z.infer<typeof subscriptionIntervalSchema>

export const PLAN_LOOKUP_KEY_MAP: Record<PriceLookupKey, SubscriptionPlan> = {

  starter_yearly: 'starter',
  starter_monthly: 'starter',

  essential_monthly: 'essential',
  essential_yearly: 'essential',

  advanced_monthly: 'advanced',
  advanced_yearly: 'advanced',

  enterprise_yearly: 'enterprise',
  enterprise_monthly: 'enterprise'
};

export const INTERVAL_LOOKUP_KEY_MAP: Record<PriceLookupKey, SubscriptionInterval> = {
  starter_yearly: 'year',
  starter_monthly: 'month',

  essential_monthly: 'month',
  essential_yearly: 'year',

  advanced_monthly: 'month',
  advanced_yearly: 'year',

  enterprise_yearly: 'year',
  enterprise_monthly: 'month'
};


export const subscriptionInputValuesSchema = z.object({
  promoCode: z.string().optional(),
  priceLookupKey: priceLookupKeySchema,
  enableAutoRenewal: z.boolean().optional()
});

export type SubscriptionInputValues = z.infer<typeof subscriptionInputValuesSchema>

export type DiscountType = {
  id: string,
  coupon: {
    id: string,
    amount_off: number,
    name: string,
  }
}

export const promoCodeTypesScema = z.enum(['user', 'partner', 'generic']);
export type promoCodeTypes = z.infer<typeof promoCodeTypesScema>

export type PromoCodeAttrs = {
  trial_days: number,
  price_lookup_key: PriceLookupKey,
  stripe_coupon_id: string,
  user_id: string | null,
  salesman_id: string | null,
  promo_type: promoCodeTypes
  id: string,
  couponId: string,
}

export const PaymentMethodAttrsSchema = z.object({
  id: z.string(),
  type: z.string(),
  cardBrand: z.string().nullish(),
  last4: z.string().nullish()
});

type RawPaymentMethodAttrs = z.infer<typeof PaymentMethodAttrsSchema>

export type PaymentMethodAttrs = Omit<RawPaymentMethodAttrs, 'type'> & { type: Stripe.PaymentMethod.Type }

export const subscriptionAttrsSchema = z.object({
  id: z.string(),
  status: z.enum(subscriptionStatuses),
  default_payment_method: PaymentMethodAttrsSchema.optional(),
  trial_days_left: z.number().optional(),
  interval: z.enum(['year', 'month', 'day', 'week']),
  extra_sales_points_number: z.number(),
  cancel_at: z.number().nullish(),
  cancel_at_period_end: z.boolean(),
  renewal_date: z.string(),
  subscription_amount: z.number(),
  extra_sales_points_subscriptions_amount: z.number().optional(),
  is_subscription_valid: z.boolean(),
  subscription_plan: subscriptionPlanSchema,
  subscription_price_lookup_key: priceLookupKeySchema
});

export type SubscriptionAttrs = z.infer<typeof subscriptionAttrsSchema>


export const PromoSchema = z.object({
  code: z.string(),
  disclaimer_it: z.string(),
  title_it: z.string(),
  price_lookup_key: priceLookupKeySchema,
  old_price: z.string(),
  new_price: z.string(),
  price_time_range_it: z.string()
});

export type Promo = z.infer<typeof PromoSchema>

export const planFeaturesSummary: Record<SubscriptionPlan, string[]> = {
  starter: [
    'Fino a 100 clienti',
    '2 traguardi',
    '1 punto vendita incluso',
    'Statistiche di base',
    'Notifiche illimitate'
  ],
  essential: [
    'Fino a 1000 clienti',
    'Fino 4 traguardi',
    '3 punti vendita inclusi',
    'Statistiche per punto vendita',
    'Notifiche illimitate'
  ],
  advanced: [
    'Clienti illimitati',
    'Fino a 20 traguardi',
    '10 punti vendita inclusi',
    'Gestione e segmentazione clienti',
    'Notifiche avanzate e video',
    'Accesso API e strumenti per sviluppatori',
    'Permessi staff avanzati'
  ],
  enterprise: [
    'Funzionalità illimitate',
    'Supporto per integrazioni personalizzate',
    'Onboarding 1:1 dedicato + SLA',
    'Supporto prioritario'
  ]
};
